/**
 * SqlButtonElement — kreator formatek: klocek palety „Przycisk SQL" (`sql_button`, patrz
 * `RecursiveFormRenderer` PLACEHOLDER_TYPES).
 *
 * Admin stawia go w dowolnej sekcji, nadaje napis i wybiera zapisaną procedurę „Na przycisk"
 * tej kartoteki (Ustawienia → Procedury SQL → Kartoteki). Na stronie rekordu kliknięcie zapisuje
 * rekord i uruchamia procedurę — to robi renderer podpięty w `contextRenderers['sql_button']`
 * strony rekordu, nie ten plik. Ten plik obsługuje TYLKO kreator: panel właściwości i podgląd na
 * płótnie (`mode='builder'`); poza kreatorem `render` nie rysuje nic, żeby bez renderera strony
 * nie pokazać martwego przycisku.
 *
 * Ustawienia klocka (`element.settings`): `{ label: string, procedureId: string }` — jak
 * `additional_query_view` trzyma `viewSlug`. Strona rekordu czyta dokładnie te dwa pola; napis ma
 * najwyżej `SQL_BUTTON_LABEL_MAX_LENGTH` (60) znaków. Klocek bez procedury albo z pustym napisem da się
 * zapisać, ale strona rekordu go nie rysuje — panel ostrzega, a płótno oznacza go „Niekompletny"
 * (osobny znacznik od „Procedura usunięta").
 *
 * `allowedModules` i mapowanie „slug modułu → kartoteka katalogu procedur" żyją w
 * `lib/record-procedures/sql-button-hosts.ts` (dołożenie kartoteki = jeden wiersz tam).
 *
 * Lista procedur: `GET /api/record-procedures/button-options` — wymaga prawa edycji układu, nie
 * prawa katalogu procedur, i niesie wyłącznie id + nazwę + znacznik aktywności (bez SQL).
 * Procedury usunięte wracają z `active: false`: nie da się ich wybrać, a zapisane na klocku
 * pokazują znacznik „Procedura usunięta" (panel i płótno).
 *
 * `render`/`propertiesEditor` są wołane jak ZWYKŁE FUNKCJE — hooki żyją w komponentach-dzieciach.
 * Kreator bez danych tenanta (osadzony w panelu ITE; `useBuilderEnvironment().tenantData ===
 * false`) nie pyta o procedury wcale: panel pokazuje informację, płótno — sam przycisk.
 * Płótno zna kartotekę z `useBuilderEnvironment().moduleSlug` (render nie dostaje slugu modułu).
 */

import { type CSSProperties, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type {
  LayoutElement,
  LayoutElementDefinition,
  LayoutElementPropertiesEditorProps,
} from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';
import {
  parseSqlButtonSettings,
  SQL_BUTTON_LABEL_MAX_LENGTH,
} from '@/components/record-procedures/sql-button-settings';
import {
  buttonOptionsErrorStatus,
  useRecordProcedureButtonOptions,
} from '@/lib/record-procedures/api';
import {
  SQL_BUTTON_ALLOWED_MODULES,
  sqlButtonTargetForModule,
} from '@/lib/record-procedures/sql-button-hosts';
import { cn } from '@/lib/utils';

// ─── Ustawienia ─────────────────────────────────────────────────────────────

/** Odczyt `label` / `procedureId` z luźnych ustawień elementu (brak lub zły typ = pusty tekst). */
function readSettings(settings: Record<string, unknown> | undefined): {
  label: string;
  procedureId: string;
} {
  return {
    label: typeof settings?.label === 'string' ? settings.label : '',
    procedureId: typeof settings?.procedureId === 'string' ? settings.procedureId : '',
  };
}

// ─── Panel właściwości ──────────────────────────────────────────────────────

interface ProcedureFieldProps {
  moduleSlug: string | undefined;
  procedureId: string;
  onSelect: (procedureId: string) => void;
}

/**
 * Pole „Procedura". Bramka środowiska: lista procedur to dane TENANTA, więc w kreatorze bez
 * dostępu do nich (panel ITE) pokazujemy tylko informację i NIE montujemy komponentu, który
 * odpytuje API (jego hook żyje w `ProcedureOptions`).
 */
function ProcedureField({ moduleSlug, procedureId, onSelect }: ProcedureFieldProps) {
  const { t } = useTranslation();
  const { tenantData } = useBuilderEnvironment();
  const labelId = useId();

  return (
    <div className="space-y-1.5" data-testid="sql-button-procedure-field">
      <span id={labelId} className="block text-sm font-medium text-text-secondary">
        {t('recordProcedures.builderBlock.procedureField')}
      </span>
      {tenantData ? (
        <ProcedureOptions
          moduleSlug={moduleSlug}
          procedureId={procedureId}
          onSelect={onSelect}
          labelId={labelId}
        />
      ) : (
        <p className="text-sm text-text-tertiary" data-testid="sql-button-procedure-unavailable">
          {t('recordProcedures.builderBlock.unavailableHere')}
        </p>
      )}
    </div>
  );
}

function ProcedureOptions({
  moduleSlug,
  procedureId,
  onSelect,
  labelId,
}: ProcedureFieldProps & { labelId: string }) {
  const { t } = useTranslation();
  const target = sqlButtonTargetForModule(moduleSlug);
  const optionsQuery = useRecordProcedureButtonOptions(target);

  // Moduł spoza mapy (palety nie oferują tam klocka — `allowedModules` to te same slugi) nie ma
  // czego wybierać; zapytanie jest wtedy wyłączone, więc nie czekamy na „ładowanie".
  if (target !== undefined && optionsQuery.isPending) {
    return (
      <p className="text-sm text-text-muted" data-testid="sql-button-procedure-loading">
        {t('common.loading')}
      </p>
    );
  }

  if (optionsQuery.isError) {
    // 403 = brak prawa edycji układów (to samo prawo bramkuje zapis układu); inne błędy — ogólnie.
    const forbidden = buttonOptionsErrorStatus(optionsQuery.error) === 403;
    return (
      <p className="text-sm text-danger-600" role="alert" data-testid="sql-button-procedure-error">
        {t(
          forbidden
            ? 'recordProcedures.builderBlock.forbidden'
            : 'recordProcedures.builderBlock.loadError',
        )}
      </p>
    );
  }

  // Do wyboru tylko aktywne; usunięta procedura zostaje widoczna wyłącznie jako ostrzeżenie.
  const active = (optionsQuery.data ?? []).filter((option) => option.active);
  const selectedIsActive = active.some((option) => option.id === procedureId);
  const selectedIsMissing = procedureId !== '' && !selectedIsActive;
  const deletedNotice = selectedIsMissing ? (
    <p className="text-xs text-danger-600" role="alert" data-testid="sql-button-procedure-deleted">
      {t('recordProcedures.builderBlock.deletedNotice')}
    </p>
  ) : null;

  if (active.length === 0) {
    return (
      <>
        <p className="text-sm text-text-tertiary" data-testid="sql-button-procedure-empty">
          {t('recordProcedures.builderBlock.noProcedures')}
        </p>
        {deletedNotice}
      </>
    );
  }

  return (
    <>
      <p className="text-xs text-text-muted">{t('recordProcedures.builderBlock.procedureHint')}</p>
      <Dropdown
        value={selectedIsActive ? procedureId : ''}
        onChange={onSelect}
        aria-labelledby={labelId}
        aria-required
        data-testid="sql-button-procedure-select"
        options={[
          { value: '', label: t('recordProcedures.builderBlock.procedurePlaceholder') },
          ...active.map((option) => ({ value: option.id, label: option.name })),
        ]}
      />
      {deletedNotice}
    </>
  );
}

function SqlButtonPropertiesEditor({
  settings,
  onChange,
  moduleSlug,
}: LayoutElementPropertiesEditorProps) {
  const { t } = useTranslation();
  const { label, procedureId } = readSettings(settings);

  return (
    <div className="space-y-3" data-testid="sql-button-properties-editor">
      <Input
        size="sm"
        label={t('recordProcedures.builderBlock.labelField')}
        required
        maxLength={SQL_BUTTON_LABEL_MAX_LENGTH}
        value={label}
        onChange={(e) => onChange({ label: e.target.value })}
        error={label.trim() === '' ? t('recordProcedures.builderBlock.labelRequired') : undefined}
        data-testid="sql-button-prop-label"
      />
      <ProcedureField
        moduleSlug={moduleSlug}
        procedureId={procedureId}
        onSelect={(next) => onChange({ procedureId: next })}
      />
      {/* Bez procedury strona rekordu nie rysuje przycisku — admin ma to zobaczyć już w kreatorze. Alert
          w tonie ostrzeżenia (bez `role="alert"`: to podpowiedź przy polu, nie błąd zapisu). */}
      {procedureId.trim() === '' && (
        <Alert tone="warning" data-testid="sql-button-procedure-incomplete">
          {t('recordProcedures.builderBlock.incompleteWarning')}
        </Alert>
      )}
    </div>
  );
}

// ─── Płótno ─────────────────────────────────────────────────────────────────

interface CanvasViewProps {
  element: LayoutElement;
  /** Zapisana procedura nie jest na liście aktywnych (usunięta albo nieistniejąca). */
  procedureMissing: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Przycisk z napisem w przerywanej ramce; przy brakującej procedurze czerwona ramka i znacznik
 * „Procedura usunięta", a przy klocku, którego strona rekordu nie narysuje (pusty napis albo brak
 * wybranej procedury), znacznik „Niekompletny" — pusty napis nie udaje działającego przycisku.
 * Przycisk jest tylko obrazkiem — nie reaguje na klik ani Tab, więc klik zaznacza klocek w
 * kreatorze jak każdy inny.
 */
function SqlButtonCanvasView({ element, procedureMissing, className, style }: CanvasViewProps) {
  const { t } = useTranslation();
  const { label } = readSettings(element.settings);
  const text = label.trim() !== '' ? label : t('recordProcedures.builderBlock.paletteLabel');
  // Ta sama reguła co na stronie rekordu: tam klocek, który nie przejdzie odczytu ustawień, znika.
  const incomplete = parseSqlButtonSettings(element.settings) === null;

  return (
    <div
      className={cn(
        'rounded-md border border-dashed p-2.5',
        procedureMissing
          ? 'border-danger-300 bg-danger-50/50'
          : 'border-border-secondary bg-bg-secondary',
        className,
      )}
      style={style}
      data-testid={`sql-button-element-${element.id}`}
      data-element-type="sql_button"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          tabIndex={-1}
          className="pointer-events-none"
          leftIcon={<Play className="h-3.5 w-3.5" aria-hidden="true" />}
        >
          {text}
        </Button>
        {procedureMissing && (
          <Badge tone="danger" data-testid={`sql-button-element-${element.id}-deleted`}>
            {t('recordProcedures.builderBlock.deletedBadge')}
          </Badge>
        )}
        {incomplete && (
          <Badge tone="warning" data-testid={`sql-button-element-${element.id}-incomplete`}>
            {t('recordProcedures.builderBlock.incompleteBadge')}
          </Badge>
        )}
      </div>
    </div>
  );
}

/** Płótno z zapytaniem o procedury — montowane tylko, gdy kreator ma dane tenanta. */
function SqlButtonCanvasLive({
  moduleSlug,
  ...view
}: Omit<CanvasViewProps, 'procedureMissing'> & { moduleSlug: string | undefined }) {
  const optionsQuery = useRecordProcedureButtonOptions(sqlButtonTargetForModule(moduleSlug));
  const { procedureId } = readSettings(view.element.settings);
  // Brak znacznika, dopóki lista się nie wczyta albo gdy jej nie da się pobrać (np. 403): bez listy
  // nie wiadomo, czy procedura istnieje — lepiej nie straszyć.
  const procedureMissing =
    procedureId !== '' &&
    optionsQuery.isSuccess &&
    !optionsQuery.data.some((option) => option.id === procedureId && option.active);
  return <SqlButtonCanvasView {...view} procedureMissing={procedureMissing} />;
}

/**
 * Bramka środowiska płótna: kreator bez danych tenanta (panel ITE) rysuje sam przycisk, bez
 * zapytań. `className`/`style` przekazuje do korzenia `LayoutElementRegistry.applyElementStyle`
 * (klonuje zwrócony element i dokłada styl elementu).
 */
function SqlButtonCanvas(props: Omit<CanvasViewProps, 'procedureMissing'>) {
  const { tenantData, moduleSlug } = useBuilderEnvironment();
  return tenantData ? (
    <SqlButtonCanvasLive {...props} moduleSlug={moduleSlug} />
  ) : (
    <SqlButtonCanvasView {...props} procedureMissing={false} />
  );
}

// ─── Definicja klocka ───────────────────────────────────────────────────────

export const SqlButtonElement: LayoutElementDefinition = {
  type: 'sql_button',
  category: 'tresc',
  isContainer: false,
  label: 'Przycisk SQL',
  labelKey: 'recordProcedures.builderBlock.paletteLabel',
  icon: 'Play',
  allowedModules: [...SQL_BUTTON_ALLOWED_MODULES],
  defaultSettings: () => ({ label: '', procedureId: '' }),
  propertiesEditor: (props) => <SqlButtonPropertiesEditor {...props} />,
  // Płótno tylko w kreatorze; poza nim realny przycisk rysuje renderer strony rekordu.
  render: ({ element, mode }) =>
    mode === 'builder' ? <SqlButtonCanvas element={element} /> : null,
};
